<script setup>
import { ref, computed, watch, onMounted, nextTick, useSlots } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { nomeDaEtiqueta } from 'dashboard/helper/nomeDaEtiqueta';

const props = defineProps({
  conversationLabels: {
    type: Array,
    required: true,
  },
});

const slots = useSlots();
const accountLabels = useMapGetter('labels/getLabels');

const activeLabels = computed(() => {
  return accountLabels.value
    .filter(({ title }) => props.conversationLabels.includes(title))
    .toSorted((a, b) => {
      if (a.title.toLowerCase() === 'cliente') return -1;
      if (b.title.toLowerCase() === 'cliente') return 1;
      return 0;
    });
});

const showAllLabels = ref(false);
const showExpandLabelButton = ref(false);
const labelPosition = ref(-1);
const labelContainer = ref(null);

const computeVisibleLabelPosition = () => {
  const beforeSlot = slots.before ? 100 : 0;
  if (!labelContainer.value) {
    return;
  }

  const labels = Array.from(
    labelContainer.value.querySelectorAll('.pill-label')
  );
  let labelOffset = 0;
  showExpandLabelButton.value = false;
  labels.forEach((label, index) => {
    labelOffset += label.offsetWidth + 6;

    if (labelOffset < labelContainer.value.clientWidth - beforeSlot) {
      labelPosition.value = index;
    } else {
      showExpandLabelButton.value = labels.length > 1;
    }
  });
};

watch(activeLabels, () => {
  nextTick(() => computeVisibleLabelPosition());
});

onMounted(() => {
  computeVisibleLabelPosition();
});

const onShowLabels = e => {
  e.stopPropagation();
  showAllLabels.value = !showAllLabels.value;
  nextTick(() => computeVisibleLabelPosition());
};
</script>

<template>
  <div ref="labelContainer" v-resize="computeVisibleLabelPosition">
    <div
      v-if="activeLabels.length || $slots.before"
      class="flex items-center min-w-0 gap-y-1 gap-x-1.5"
      :class="{ 'h-auto overflow-visible flex-row flex-wrap': showAllLabels }"
    >
      <slot name="before" />
      <span
        v-for="(label, index) in activeLabels"
        :key="label ? label.id : index"
        v-tooltip.top="label.description || label.title"
        class="pill-label cl-etq"
        :style="{ '--etq': label.color }"
        :class="{
          'invisible absolute': !showAllLabels && index > labelPosition,
        }"
      >
        <span class="cl-etq__ponto" aria-hidden="true" />
        <span class="cl-etq__nome">{{ nomeDaEtiqueta(label.title) }}</span>
      </span>
      <button
        v-if="showExpandLabelButton"
        v-tooltip.top="
          showAllLabels
            ? $t('CONVERSATION.CARD.HIDE_LABELS')
            : $t('CONVERSATION.CARD.SHOW_LABELS')
        "
        type="button"
        class="cl-mais"
        :aria-label="
          showAllLabels
            ? $t('CONVERSATION.CARD.HIDE_LABELS')
            : $t('CONVERSATION.CARD.SHOW_LABELS')
        "
        :aria-expanded="showAllLabels"
        @click="onShowLabels"
      >
        <span
          :class="
            showAllLabels ? 'i-lucide-chevron-left' : 'i-lucide-chevron-right'
          "
          class="size-3"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
/* A etiqueta do cartão da lista: ponto na cor que a pessoa escolheu e fundo
   tingido dela; o texto fica no tom do produto. Era pílula CHEIA na cor com
   texto calculado, em caixa alta — nas cores vivas brigava com o contador de
   não lidas pela atenção, e o nome ("PENSAO") perdia a leitura. */
.cl-etq {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  flex-shrink: 0;
  max-width: calc(100% - var(--esp-2));
  height: 22px;
  padding: 0 var(--esp-2);
  border-radius: var(--r-redondo);
  background: color-mix(in srgb, var(--etq) 14%, rgb(var(--solid-1)));
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  color: rgb(var(--slate-12));
  white-space: nowrap;
}

.cl-etq__ponto {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--r-redondo);
  background: var(--etq);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--etq) 25%, transparent);
}

.cl-etq__nome {
  overflow: hidden;
  text-overflow: ellipsis;
}

.cl-mais {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: var(--r-1);
  background: none;
  color: rgb(var(--slate-10));
  cursor: pointer;
  transition: color var(--mov-1) var(--mov-curva);
}

.cl-mais:hover {
  color: rgb(var(--slate-12));
}
</style>
